import React, { useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import api from '../../services/api';
import { transactionService } from '../../services/transactionService';

// Stala poza komponentem - inaczej useMemo mialby ja jako zmieniajaca sie zaleznosc.
const monthNames = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

const SalesMarginReport = ({ marginReports, locationId, selectedLocation }) => {
  const [marginRangeReport, setMarginRangeReport] = useState(null);
  const [marginRangeLoading, setMarginRangeLoading] = useState(false);
  const [marginDateRange, setMarginDateRange] = useState({
    dateFrom: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0]
  });

  /**
   * Estymacja biezacego miesiaca - prognoza z dotychczasowego tempa.
   * Liczona LOKALNIE z danych, ktore raport i tak juz pobral (monthly + daily_history),
   * zeby nie dokladac zapytan do bazy.
   *
   * Tempo = wynik dotychczasowy / liczba dni ZE SPRZEDAZA (nie kalendarzowych - sklep
   * nie handluje codziennie). Przewidywana liczba dni sprzedazowych w miesiacu wynika
   * z dotychczasowego rytmu (dni ze sprzedaza / dni minione), wiec zamkniete niedziele
   * nie zawyzaja prognozy.
   */
  const estimation = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const mNum = now.getMonth() + 1;
    const current = (marginReports?.monthly || []).find(
      (m) => Number(m.year) === y && Number(m.month_num) === mNum
    );
    if (!current) return null;

    const prefix = `${y}-${String(mNum).padStart(2, '0')}`;
    const salesDays = (marginReports?.daily_history || []).filter(
      (d) => String(d.date || '').startsWith(prefix) && parseFloat(d.revenue_netto) > 0
    ).length;

    const daysElapsed = now.getDate();
    const daysInMonth = new Date(y, mNum, 0).getDate();
    // Ponizej 3 dni ze sprzedaza prognoza jest zgadywaniem - lepiej jej nie pokazywac
    // niz podac liczbe, na ktorej ktos oprze decyzje.
    if (salesDays < 3) return { tooEarly: true, salesDays };

    const revenue = parseFloat(current.revenue_netto) || 0;
    const revenueBrutto = parseFloat(current.revenue_brutto) || 0;
    const margin = parseFloat(current.margin_netto) || 0;
    const cost = parseFloat(current.purchase_cost_netto) || 0;
    const projectedSalesDays = Math.max(
      salesDays,
      Math.round((salesDays / daysElapsed) * daysInMonth)
    );
    const factor = projectedSalesDays / salesDays;

    const estRevenue = revenue * factor;
    const estRevenueBrutto = revenueBrutto * factor;
    const estMargin = margin * factor;
    const estCost = cost * factor;

    // Poprzedni pelny miesiac - do porownania "czy idziemy lepiej niz ostatnio"
    const prevD = new Date(y, mNum - 2, 1);
    const prev = (marginReports?.monthly || []).find(
      (m) => Number(m.year) === prevD.getFullYear() && Number(m.month_num) === prevD.getMonth() + 1
    );
    const prevRevenue = prev ? parseFloat(prev.revenue_netto) || 0 : null;
    const changePct = prevRevenue ? ((estRevenue - prevRevenue) / prevRevenue) * 100 : null;

    return {
      tooEarly: false,
      salesDays,
      daysElapsed,
      daysInMonth,
      projectedSalesDays,
      revenue,
      revenueBrutto,
      margin,
      estRevenue,
      estRevenueBrutto,
      estMargin,
      estCost,
      estMarginPct: estRevenue > 0 ? (estMargin / estRevenue) * 100 : 0,
      perDayRevenue: revenue / salesDays,
      perDayRevenueBrutto: revenueBrutto / salesDays,
      perDayMargin: margin / salesDays,
      prevRevenue,
      prevMonthName: `${monthNames[prevD.getMonth()]} ${prevD.getFullYear()}`,
      changePct,
      monthName: `${monthNames[mNum - 1]} ${y}`,
    };
  }, [marginReports]);

  const loadMarginRangeReport = async () => {
    try {
      setMarginRangeLoading(true);
      const response = await transactionService.getMarginReportsRange(
        marginDateRange.dateFrom,
        marginDateRange.dateTo,
        locationId
      );
      if (response.success && response.data) {
        setMarginRangeReport(response.data);
      }
    } catch (err) {
      console.error('Blad podczas pobierania raportu marzy dla zakresu:', err);
    } finally {
      setMarginRangeLoading(false);
    }
  };

  const printMarginRangeReport = async () => {
    if (!marginRangeReport) return;
    try {
      const params = new URLSearchParams({
        date_from: marginDateRange.dateFrom,
        date_to: marginDateRange.dateTo
      });
      if (locationId) params.append('location_id', locationId);
      const response = await api.get(`/pos/margin-reports/range/pdf?${params}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Blad generowania PDF raportu marzy.');
    }
  };

  return (
    <div>
      {/* Raport dla wybranego zakresu dat */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)', marginBottom: '0.75rem' }}>
        <div style={{ padding: '0.5rem', border: '1px solid var(--orange)', borderBottom: '1px solid var(--border)', borderRadius: '0.375rem 0.375rem 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: 'var(--orange)' }}>📊 Raport marży dla wybranego okresu</h5>
          {marginRangeReport && (
            <button
              onClick={printMarginRangeReport}
              style={{
                padding: '4px 10px',
                fontSize: '11px',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--orange)',
                fontWeight: '600'
              }}
            >
              🖨️ Drukuj raport
            </button>
          )}
        </div>
        <div style={{ padding: '0.75rem' }}>
          {/* Wybór zakresu dat */}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'end', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', display: 'block', marginBottom: '0.15rem' }}>Data od:</label>
              <input
                type="date"
                value={marginDateRange.dateFrom}
                onChange={(e) => setMarginDateRange(prev => ({ ...prev, dateFrom: e.target.value }))}
                style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
              />
            </div>
            <div>
              <label style={{ fontSize: '11px', fontWeight: '500', color: 'var(--text-muted)', display: 'block', marginBottom: '0.15rem' }}>Data do:</label>
              <input
                type="date"
                value={marginDateRange.dateTo}
                onChange={(e) => setMarginDateRange(prev => ({ ...prev, dateTo: e.target.value }))}
                style={{ padding: '6px 10px', fontSize: '12px', border: '1px solid var(--border)', borderRadius: '6px', backgroundColor: 'var(--card-bg)', color: 'var(--text)' }}
              />
            </div>
            <button
              onClick={loadMarginRangeReport}
              disabled={marginRangeLoading}
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                border: `1px solid ${marginRangeLoading ? 'var(--text-muted)' : 'var(--orange)'}`,
                color: marginRangeLoading ? 'var(--text-muted)' : 'var(--orange)',
                fontWeight: '600'
              }}
            >
              {marginRangeLoading ? '⏳ Ładowanie...' : '🔍 Generuj raport'}
            </button>
          </div>

          {/* Wyniki raportu */}
          {marginRangeReport && (
            <>
              {/* Podsumowanie */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <div style={{ border: '1px solid var(--border)', padding: '0.4rem', textAlign: 'center', borderRadius: '0.25rem', backgroundColor: 'var(--bg)' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--text)' }}>{marginRangeReport.days_count}</div>
                  <small style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>Dni</small>
                </div>
                <div style={{ border: '1px solid var(--border)', padding: '0.4rem', textAlign: 'center', borderRadius: '0.25rem', backgroundColor: 'var(--bg)' }}>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--text)' }}>{marginRangeReport.summary?.total_transactions || 0}</div>
                  <small style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>Transakcji</small>
                </div>
                <div style={{ border: '1px solid var(--border)', padding: '0.4rem', textAlign: 'center', borderRadius: '0.25rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--info)' }}>{(marginRangeReport.summary?.total_revenue_netto || 0).toFixed(2)} zł</div>
                  <small style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>Przychód netto</small>
                </div>
                <div style={{ border: '1px solid var(--border)', padding: '0.4rem', textAlign: 'center', borderRadius: '0.25rem' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--danger)' }}>{(marginRangeReport.summary?.total_purchase_cost_netto || 0).toFixed(2)} zł</div>
                  <small style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>Koszt netto</small>
                </div>
                <div style={{ border: '1px solid var(--border)', padding: '0.4rem', textAlign: 'center', borderRadius: '0.25rem', backgroundColor: 'var(--success-light)' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--success)' }}>{(marginRangeReport.summary?.total_margin_netto || 0).toFixed(2)} zł</div>
                  <small style={{ fontSize: '0.6rem', color: 'var(--success)' }}>Marża netto</small>
                </div>
                <div style={{ border: '1px solid var(--border)', padding: '0.4rem', textAlign: 'center', borderRadius: '0.25rem', backgroundColor: 'var(--info-light)' }}>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', color: 'var(--info)' }}>{(marginRangeReport.summary?.avg_margin_percent || 0).toFixed(1)}%</div>
                  <small style={{ fontSize: '0.6rem', color: 'var(--info)' }}>Średnia marża</small>
                </div>
              </div>

              {/* Tabela dzienna */}
              {marginRangeReport.daily_data?.length > 0 && (
                <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.7rem' }}>
                    <thead>
                      <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--orange-light)' }}>
                        <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--orange)', textAlign: 'left' }}>Data</th>
                        <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--orange)', textAlign: 'right' }}>Trans.</th>
                        <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--orange)', textAlign: 'right' }}>Szt.</th>
                        <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--orange)', textAlign: 'right' }}>Przychód netto</th>
                        <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--orange)', textAlign: 'right' }}>Koszt netto</th>
                        <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--orange)', textAlign: 'right' }}>Marża netto</th>
                        <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--orange)', textAlign: 'right' }}>%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {marginRangeReport.daily_data.map((day, index) => (
                        <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--warning-light)' }}>
                          <td style={{ padding: '0.25rem', borderBottom: '1px solid var(--border)' }}>{day.date}</td>
                          <td style={{ padding: '0.25rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>{day.transactions_count}</td>
                          <td style={{ padding: '0.25rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>{parseFloat(day.items_sold).toFixed(0)}</td>
                          <td style={{ padding: '0.25rem', borderBottom: '1px solid var(--border)', textAlign: 'right', color: 'var(--info)' }}>{parseFloat(day.revenue_netto).toFixed(2)} zł</td>
                          <td style={{ padding: '0.25rem', borderBottom: '1px solid var(--border)', textAlign: 'right', color: 'var(--danger)' }}>{parseFloat(day.purchase_cost_netto).toFixed(2)} zł</td>
                          <td style={{ padding: '0.25rem', borderBottom: '1px solid var(--border)', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>{parseFloat(day.margin_netto).toFixed(2)} zł</td>
                          <td style={{ padding: '0.25rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                            <span style={{
                              backgroundColor: day.margin_percent >= 30 ? 'var(--success)' : day.margin_percent >= 20 ? 'var(--warning)' : 'var(--danger)',
                              color: '#fff',
                              padding: '0.1rem 0.2rem',
                              borderRadius: '0.15rem',
                              fontSize: '0.6rem'
                            }}>
                              {parseFloat(day.margin_percent).toFixed(1)}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {!marginRangeReport && (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem', padding: '1rem' }}>
              Wybierz zakres dat i kliknij "Generuj raport"
            </div>
          )}
        </div>
      </div>

      {/* Podsumowanie dzienne */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)', marginBottom: '0.75rem' }}>
        <div style={{ padding: '0.5rem', border: '1px solid var(--success)', borderBottom: '1px solid var(--border)', borderRadius: '0.375rem 0.375rem 0 0' }}>
          <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: 'var(--success)' }}>💰 Marża netto dzisiaj ({marginReports.daily?.date || '-'})</h5>
        </div>
        <div style={{ padding: '0.75rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem' }}>
            <div style={{ border: '1px solid var(--border)', padding: '0.5rem', textAlign: 'center', borderRadius: '0.25rem' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--success)' }}>
                {marginReports.daily?.transactions_count || 0}
              </h4>
              <small style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Transakcji</small>
            </div>
            <div style={{ border: '1px solid var(--border)', padding: '0.5rem', textAlign: 'center', borderRadius: '0.25rem' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--info)' }}>
                {parseFloat(marginReports.daily?.revenue_netto || 0).toFixed(2)} zł
              </h4>
              <small style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Przychód netto</small>
            </div>
            <div style={{ border: '1px solid var(--border)', padding: '0.5rem', textAlign: 'center', borderRadius: '0.25rem' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--danger)' }}>
                {parseFloat(marginReports.daily?.purchase_cost_netto || 0).toFixed(2)} zł
              </h4>
              <small style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Koszt zakupu netto</small>
            </div>
            <div style={{ border: '1px solid var(--border)', padding: '0.5rem', textAlign: 'center', borderRadius: '0.25rem', backgroundColor: 'var(--success-light)' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--success)' }}>
                {parseFloat(marginReports.daily?.margin_netto || 0).toFixed(2)} zł
              </h4>
              <small style={{ fontSize: '0.7rem', color: 'var(--success)' }}>Marża netto</small>
            </div>
            <div style={{ border: '1px solid var(--border)', padding: '0.5rem', textAlign: 'center', borderRadius: '0.25rem', backgroundColor: 'var(--info-light)' }}>
              <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', color: 'var(--info)' }}>
                {parseFloat(marginReports.daily?.margin_percent || 0).toFixed(1)}%
              </h4>
              <small style={{ fontSize: '0.7rem', color: 'var(--info)' }}>Marża %</small>
            </div>
          </div>
        </div>
      </div>

      {/* Estymacja biezacego miesiaca */}
      {estimation && (
        <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)', marginBottom: '0.75rem' }}>
          <div style={{ padding: '0.5rem', border: '1px solid var(--purple)', borderBottom: '1px solid var(--border)', borderRadius: '0.375rem 0.375rem 0 0' }}>
            <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: 'var(--purple)' }}>
              🔮 Estymacja sprzedaży — {estimation.monthName}
            </h5>
          </div>
          <div style={{ padding: '0.75rem' }}>
            {estimation.tooEarly ? (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                Za mało danych na prognozę — potrzebne min. 3 dni ze sprzedażą w tym miesiącu
                (jest {estimation.salesDays}).
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.6rem' }}>
                  <div style={{ flex: '1 1 150px', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.25rem' }}>
                    <small style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block' }}>Prognoza przychodu netto</small>
                    <strong style={{ fontSize: '1.1rem', color: 'var(--info)' }}>{estimation.estRevenue.toFixed(2)} zł</strong>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                      dotychczas {estimation.revenue.toFixed(2)} zł
                    </div>
                  </div>
                  <div style={{ flex: '1 1 150px', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.25rem' }}>
                    <small style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block' }}>Prognoza przychodu brutto</small>
                    <strong style={{ fontSize: '1.1rem', color: 'var(--info)' }}>{estimation.estRevenueBrutto.toFixed(2)} zł</strong>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                      dotychczas {estimation.revenueBrutto.toFixed(2)} zł
                    </div>
                  </div>
                  <div style={{ flex: '1 1 150px', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.25rem' }}>
                    <small style={{ fontSize: '0.7rem', color: 'var(--success)', display: 'block' }}>Prognoza marży netto</small>
                    <strong style={{ fontSize: '1.1rem', color: 'var(--success)' }}>{estimation.estMargin.toFixed(2)} zł</strong>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                      dotychczas {estimation.margin.toFixed(2)} zł
                    </div>
                  </div>
                  <div style={{ flex: '1 1 110px', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.25rem' }}>
                    <small style={{ fontSize: '0.7rem', color: 'var(--info)', display: 'block' }}>Marża %</small>
                    <strong style={{ fontSize: '1.1rem', color: 'var(--info)' }}>{estimation.estMarginPct.toFixed(1)}%</strong>
                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>bez zmiany tempa</div>
                  </div>
                  {estimation.changePct !== null && (
                    <div style={{ flex: '1 1 150px', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.25rem' }}>
                      <small style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block' }}>vs {estimation.prevMonthName}</small>
                      <strong style={{ fontSize: '1.1rem', color: estimation.changePct >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                        {estimation.changePct >= 0 ? '▲ +' : '▼ '}{estimation.changePct.toFixed(1)}%
                      </strong>
                      <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)' }}>
                        wtedy {estimation.prevRevenue.toFixed(2)} zł
                      </div>
                    </div>
                  )}
                </div>
                {/* Podstawa wyliczenia - zeby liczba nie byla "magiczna" */}
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                  Podstawa: {estimation.salesDays} dni ze sprzedażą z {estimation.daysElapsed} minionych
                  (średnio {estimation.perDayRevenue.toFixed(2)} zł przychodu i {estimation.perDayMargin.toFixed(2)} zł marży dziennie).
                  Prognoza zakłada {estimation.projectedSalesDays} dni sprzedażowych w całym miesiącu
                  ({estimation.daysInMonth} dni kalendarzowych) przy niezmienionym tempie.
                  To szacunek, nie zobowiązanie — im wcześniej w miesiącu, tym mniej dokładny.
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Historia dzienna (ostatnie 30 dni) */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)', marginBottom: '0.75rem' }}>
        <div style={{ padding: '0.5rem', border: '1px solid var(--info)', borderBottom: '1px solid var(--border)', borderRadius: '0.375rem 0.375rem 0 0' }}>
          <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: 'var(--info)' }}>📅 Marża dzienna (ostatnie 30 dni)</h5>
        </div>
        <div style={{ padding: '0.75rem', maxHeight: '300px', overflowY: 'auto' }}>
          {marginReports.daily_history?.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)' }}>
                  <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--border)', textAlign: 'left' }}>Data</th>
                  <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Trans.</th>
                  <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Szt.</th>
                  <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Przychód netto</th>
                  <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Koszt netto</th>
                  <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Marża netto</th>
                  <th style={{ padding: '0.3rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>%</th>
                </tr>
              </thead>
              <tbody>
                {marginReports.daily_history.map((day, index) => (
                  <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                    <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--border)' }}>{day.date}</td>
                    <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>{day.transactions_count}</td>
                    <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>{parseFloat(day.items_sold).toFixed(0)}</td>
                    <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--border)', textAlign: 'right', color: 'var(--info)' }}>{parseFloat(day.revenue_netto).toFixed(2)} zł</td>
                    <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--border)', textAlign: 'right', color: 'var(--danger)' }}>{parseFloat(day.purchase_cost_netto).toFixed(2)} zł</td>
                    <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--border)', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>{parseFloat(day.margin_netto).toFixed(2)} zł</td>
                    <td style={{ padding: '0.3rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                      <span style={{
                        backgroundColor: day.margin_percent >= 30 ? 'var(--success)' : day.margin_percent >= 20 ? 'var(--warning)' : 'var(--danger)',
                        color: '#fff',
                        padding: '0.1rem 0.25rem',
                        borderRadius: '0.2rem',
                        fontSize: '0.65rem'
                      }}>
                        {parseFloat(day.margin_percent).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>Brak danych dziennych</div>
          )}
        </div>
      </div>

      {/* Raport miesięczny */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)' }}>
        <div style={{ padding: '0.5rem', border: '1px solid var(--purple)', borderBottom: '1px solid var(--border)', borderRadius: '0.375rem 0.375rem 0 0' }}>
          <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600', color: 'var(--purple)' }}>📊 Marża miesięczna (ostatnie 12 miesięcy)</h5>
        </div>
        <div style={{ padding: '0.75rem' }}>
          {marginReports.monthly?.length > 0 ? (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem', marginBottom: '0.75rem' }}>
                <thead>
                  <tr>
                    <th style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'left' }}>Miesiąc</th>
                    <th style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Transakcji</th>
                    <th style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Sprzedano szt.</th>
                    <th style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Przychód netto</th>
                    <th style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Koszt zakupu netto</th>
                    <th style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>Marża netto</th>
                    <th style={{ padding: '0.4rem', borderBottom: '2px solid var(--border)', textAlign: 'right' }}>%</th>
                  </tr>
                </thead>
                <tbody>
                  {marginReports.monthly.map((month, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)' }}>
                      <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', fontWeight: '600' }}>
                        {monthNames[month.month_num - 1]} {month.year}
                      </td>
                      <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>{month.transactions_count}</td>
                      <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>{parseFloat(month.items_sold).toFixed(0)}</td>
                      <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'right', color: 'var(--info)' }}>{parseFloat(month.revenue_netto).toFixed(2)} zł</td>
                      <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'right', color: 'var(--danger)' }}>{parseFloat(month.purchase_cost_netto).toFixed(2)} zł</td>
                      <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'right', fontWeight: '600', color: 'var(--success)' }}>{parseFloat(month.margin_netto).toFixed(2)} zł</td>
                      <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                        <span style={{
                          backgroundColor: month.margin_percent >= 30 ? 'var(--success)' : month.margin_percent >= 20 ? 'var(--warning)' : 'var(--danger)',
                          color: '#fff',
                          padding: '0.15rem 0.3rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.7rem'
                        }}>
                          {parseFloat(month.margin_percent).toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Podsumowanie okresu */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '0.5rem', paddingTop: '0.5rem', borderTop: '2px solid var(--border)' }}>
                <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '0.25rem', padding: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Łączny przychód netto</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--info)' }}>{parseFloat(marginReports.summary?.total_revenue_netto || 0).toFixed(2)} zł</div>
                </div>
                <div style={{ backgroundColor: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '0.25rem', padding: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Łączny koszt zakupu</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--danger)' }}>{parseFloat(marginReports.summary?.total_purchase_cost_netto || 0).toFixed(2)} zł</div>
                </div>
                <div style={{ backgroundColor: 'var(--success-light)', border: '1px solid var(--success-light)', borderRadius: '0.25rem', padding: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--success)' }}>Łączna marża netto</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--success)' }}>{parseFloat(marginReports.summary?.total_margin_netto || 0).toFixed(2)} zł</div>
                </div>
                <div style={{ backgroundColor: 'var(--info-light)', border: '1px solid var(--info-light)', borderRadius: '0.25rem', padding: '0.5rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--info)' }}>Średnia marża %</div>
                  <div style={{ fontSize: '0.9rem', fontWeight: '600', color: 'var(--info)' }}>{parseFloat(marginReports.summary?.avg_margin_percent || 0).toFixed(1)}%</div>
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>Brak danych miesięcznych</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesMarginReport;
